import { useEffect, useState } from 'react';
import axios from 'axios';
import { Sparkles, Users, DollarSign, Gift, FileText, Activity } from 'lucide-react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const SESSION_FLAG = 'admin-digest-shown';

const fmtSince = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleString('en-US', {
        timeZone: 'America/Chicago',
        month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit',
    }) + ' CT';
};

export const SinceLastLoginDigest = ({ onNavigate }) => {
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        // Show only once per browser session
        if (sessionStorage.getItem(SESSION_FLAG) === '1') return;
        let cancelled = false;
        const fetchDigest = async () => {
            try {
                const res = await axios.get(`${API}/admin/since-last-login`, { withCredentials: true });
                if (cancelled) return;
                setData(res.data);
                if (res.data?.has_digest && res.data?.any_activity) {
                    setOpen(true);
                }
                sessionStorage.setItem(SESSION_FLAG, '1');
            } catch {
                // silent — modal just won't show
            }
        };
        fetchDigest();
        return () => { cancelled = true; };
    }, []);

    if (!data?.has_digest || !data?.any_activity) return null;

    const items = [
        { id: 'cashouts',     count: data.new_cashouts,     label: 'pending cashout',     plural: 'pending cashouts',     Icon: DollarSign, color: '#FF3B30', nav: { section: 'players',    tab: 'cashouts' } },
        { id: 'players',      count: data.new_players,      label: 'new player',          plural: 'new players',          Icon: Users,      color: '#39FF14', nav: { section: 'players',    tab: 'users' } },
        { id: 'promos',       count: data.promos_fired,     label: 'promo fired',         plural: 'promos fired',         Icon: Gift,       color: '#FFD700', nav: { section: 'promotions', tab: 'promo-perf' } },
        { id: 'unreconciled', count: data.unreconciled_days,label: 'unreconciled day',    plural: 'unreconciled days',    Icon: FileText,   color: '#FF8C00', nav: { section: 'reports',    tab: 'reports' } },
        { id: 'admin',        count: data.admin_actions,    label: 'admin action',        plural: 'admin actions',        Icon: Activity,   color: '#FF007F', nav: { section: 'settings',   tab: 'settings' } },
    ].filter(it => it.count > 0);

    const handleJump = (nav) => {
        setOpen(false);
        if (onNavigate) onNavigate(nav);
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent className="bg-[#121212] border-[#FFD700]/30 max-w-md" data-testid="since-last-login-modal">
                <DialogHeader>
                    <DialogTitle className="text-white flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-[#FFD700]" />
                        Welcome back
                    </DialogTitle>
                    <DialogDescription className="text-[#A0AAB2]">
                        Here's what happened since your last visit on{' '}
                        <span className="text-white font-mono">{fmtSince(data.previous_login_at)}</span>.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-2 my-2" data-testid="digest-items">
                    {items.map(it => {
                        const Icon = it.Icon;
                        return (
                            <button
                                key={it.id}
                                type="button"
                                onClick={() => handleJump(it.nav)}
                                className="w-full flex items-center gap-3 p-3 rounded-lg bg-[#0a0a0a] border border-white/5 hover:border-[#FFD700]/40 hover:bg-white/5 transition-colors text-left"
                                data-testid={`digest-item-${it.id}`}
                            >
                                <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                                    style={{ backgroundColor: it.color + '22' }}>
                                    <Icon className="w-4 h-4" style={{ color: it.color }} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-bold text-white">
                                        <span className="font-mono" style={{ color: it.color }}>{it.count}</span>{' '}
                                        new {it.count === 1 ? it.label : it.plural}
                                    </p>
                                </div>
                                <span className="text-[10px] text-[#A0AAB2] uppercase tracking-wide font-bold">View →</span>
                            </button>
                        );
                    })}
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => setOpen(false)}
                        className="border-white/10 text-[#A0AAB2] hover:text-white"
                        data-testid="digest-dismiss"
                    >
                        Dismiss
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
